import { createContext, ReactNode, useContext, useState } from 'react';
import { QueryClient } from 'react-query';
import axiosClient from '../lib/axios';
import { IPermissionContext, TProgramaOptions, TFuncionalidadeOptions, PERMISSION_MAP, TPermissionId } from '../types/permissions';

export const PermissionsContext = createContext<IPermissionContext>({
    permissoes: null,
    procurarPermissoes: async () => { },
    verificarPermissoes: () => false,
    verificarPermissaoPorId: () => false,
    removerPermissoes: () => { },
    isLoading: false
});

export function PermissionsProvider({ children }: { children: ReactNode }) {
    const [permissoes, setPermissoes] = useState<Record<TProgramaOptions, string[]> | null>(
        JSON.parse(localStorage.getItem("permissoes") || "null")
    );
    const [isLoading, setIsLoading] = useState(false);

    const procurarPermissoes = async () => {
        const queryClient = new QueryClient();
        setIsLoading(true);

        try {
            const { data } = await queryClient.fetchQuery('minhas-permissoes', () =>
                axiosClient.get("/minhas-permissoes"),
                { staleTime: 0 }
            );

            setPermissoes(data);
            localStorage.setItem("permissoes", JSON.stringify(data));
        } catch (error) {
            console.error("Erro ao buscar permissões:", error);
            removerPermissoes();
        } finally {
            setIsLoading(false);
        }
    };

    const verificarPermissoes = (programa: TProgramaOptions, funcionalidade?: TFuncionalidadeOptions): boolean => {
        if (!permissoes || Array.isArray(permissoes)) return false;

        const itemPrograma: string[] = permissoes[programa];
        if (!itemPrograma) return false;

        if (!funcionalidade) return Boolean(itemPrograma);

        const itemSubPrograma: string | undefined = itemPrograma.find((feat) => feat === funcionalidade);
        return Boolean(itemSubPrograma);
    };

    const verificarPermissaoPorId = (permissionId: TPermissionId): boolean => {
        const permissao = PERMISSION_MAP[permissionId];
        if (!permissao) {
            console.warn(`Permission ID não encontrado: ${permissionId}`);
            return false;
        }
        if ('funcionalidade' in permissao) {
            return verificarPermissoes(permissao.programa, permissao.funcionalidade as TFuncionalidadeOptions);
        }
        return verificarPermissoes(permissao.programa);
    };

    const removerPermissoes = () => {
        localStorage.removeItem("permissoes");
        setPermissoes(null);
    };

    return (
        <PermissionsContext.Provider value={{
            permissoes,
            procurarPermissoes,
            verificarPermissoes,
            verificarPermissaoPorId,
            removerPermissoes,
            isLoading
        }}>
            {children}
        </PermissionsContext.Provider>
    );
}

export function usePermissionsContext() {
    return useContext(PermissionsContext);
}